/**
 * Date and Time Formatting Utilities using dayjs
 * Standard format: yyyy-MMM-dd HH:mm:ss (24-hour clock, month in words)
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// Standard date/time format: yyyy-MMM-dd HH:mm:ss
const DATETIME_FORMAT = "YYYY-MMM-DD HH:mm:ss";
const DATE_FORMAT = "YYYY-MMM-DD";
const TIME_FORMAT = "HH:mm:ss";

/**
 * Formats a date string or Date object to the standard app format
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted date string in format: yyyy-MMM-dd HH:mm:ss
 */
export const formatDateTime = (
  date: string | Date | number | null | undefined,
): string => {
  if (!date) return "Not specified";

  try {
    const dayjsObj = dayjs(date);

    // Check if date is valid
    if (!dayjsObj.isValid()) {
      return "Invalid date";
    }

    return dayjsObj.format(DATETIME_FORMAT);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Formats a date to just the date part (without time)
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted date string in format: yyyy-MMM-dd
 */
export const formatDate = (
  date: string | Date | number | null | undefined,
): string => {
  if (!date) return "Not specified";

  try {
    const dayjsObj = dayjs(date);

    if (!dayjsObj.isValid()) {
      return "Invalid date";
    }

    return dayjsObj.format(DATE_FORMAT);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Formats a date to just the time part
 * @param date - Date string, Date object, or timestamp
 * @returns Formatted time string in format: HH:mm:ss
 */
export const formatTime = (
  date: string | Date | number | null | undefined,
): string => {
  if (!date) return "Not specified";

  try {
    const dayjsObj = dayjs(date);

    if (!dayjsObj.isValid()) {
      return "Invalid time";
    }

    return dayjsObj.format(TIME_FORMAT);
  } catch (error) {
    console.error("Error formatting time:", error);
    return "Invalid time";
  }
};

/**
 * Formats a relative time (e.g., "2 hours ago", "yesterday")
 * @param date - Date string, Date object, or timestamp
 * @returns Relative time string or formatted date if too old
 */
export const formatRelativeTime = (
  date: string | Date | number | null | undefined,
): string => {
  if (!date) return "Not specified";

  try {
    const dayjsObj = dayjs(date);

    if (!dayjsObj.isValid()) {
      return "Invalid date";
    }

    const now = dayjs();
    const diffDays = now.diff(dayjsObj, "day");

    // For dates older than 7 days, show the full formatted date
    if (diffDays > 7) {
      return formatDateTime(date);
    }

    // For recent dates, show relative time
    return dayjsObj.fromNow();
  } catch (error) {
    console.error("Error formatting relative time:", error);
    return formatDateTime(date);
  }
};

/**
 * Checks if a date is today
 * @param date - Date string, Date object, or timestamp
 * @returns Boolean indicating if the date is today
 */
export const isToday = (
  date: string | Date | number | null | undefined,
): boolean => {
  if (!date) return false;

  try {
    const dayjsObj = dayjs(date);
    return dayjsObj.isValid() && dayjsObj.isSame(dayjs(), "day");
  } catch (error) {
    return false;
  }
};

/**
 * Checks if a date is yesterday
 * @param date - Date string, Date object, or timestamp
 * @returns Boolean indicating if the date is yesterday
 */
export const isYesterday = (
  date: string | Date | number | null | undefined,
): boolean => {
  if (!date) return false;

  try {
    const dayjsObj = dayjs(date);
    return (
      dayjsObj.isValid() && dayjsObj.isSame(dayjs().subtract(1, "day"), "day")
    );
  } catch (error) {
    return false;
  }
};

/**
 * Checks if a date is overdue (past today)
 * @param date - Date string, Date object, or timestamp
 * @returns Boolean indicating if the date is overdue
 */
export const isOverdue = (
  date: string | Date | number | null | undefined,
): boolean => {
  if (!date) return false;

  try {
    const dayjsObj = dayjs(date);
    return dayjsObj.isValid() && dayjsObj.isBefore(dayjs(), "day");
  } catch (error) {
    return false;
  }
};

/**
 * Checks if a date is in the future
 * @param date - Date string, Date object, or timestamp
 * @returns Boolean indicating if the date is in the future
 */
export const isFuture = (
  date: string | Date | number | null | undefined,
): boolean => {
  if (!date) return false;

  try {
    const dayjsObj = dayjs(date);
    return dayjsObj.isValid() && dayjsObj.isAfter(dayjs());
  } catch (error) {
    return false;
  }
};

/**
 * Formats duration between two dates
 * @param startDate - Start date
 * @param endDate - End date (defaults to now)
 * @returns Formatted duration string
 */
export const formatDuration = (
  startDate: string | Date | number,
  endDate: string | Date | number = new Date(),
): string => {
  try {
    const start = dayjs.utc(startDate);
    const end = dayjs.utc(endDate);

    if (!start.isValid() || !end.isValid()) {
      return "Invalid duration";
    }

    const diffMs = end.diff(start);

    if (diffMs < 0) return "Invalid duration";

    return formatDurationFromSeconds(diffMs / 1000);
  } catch (error) {
    console.error("Error formatting duration:", error);
    return "Invalid duration";
  }
};

/**
 * Formats a duration given in seconds into a human-readable string.
 * Handles sub-second, seconds, minutes, hours, and days.
 *
 * @param totalSeconds - Duration in seconds (fractional allowed)
 * @returns Formatted string, e.g. "250ms", "12.3s", "5m 23s", "2h 10m", "3d 4h 15m"
 */
export const formatDurationFromSeconds = (totalSeconds: number): string => {
  if (totalSeconds < 0) return "Invalid duration";
  if (totalSeconds < 1) return `${Math.round(totalSeconds * 1000)}ms`;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);

  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
};

/**
 * Gets the start of the day for a given date
 * @param date - Date string, Date object, or timestamp
 * @returns dayjs object at start of day
 */
export const startOfDay = (date: string | Date | number = new Date()) => {
  return dayjs(date).startOf("day");
};

/**
 * Gets the end of the day for a given date
 * @param date - Date string, Date object, or timestamp
 * @returns dayjs object at end of day
 */
export const endOfDay = (date: string | Date | number = new Date()) => {
  return dayjs(date).endOf("day");
};

/**
 * Formats a date range
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: string | Date | number,
  endDate: string | Date | number,
): string => {
  try {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid()) {
      return "Invalid date range";
    }

    // If same day, show date once with time range
    if (start.isSame(end, "day")) {
      return `${start.format(DATE_FORMAT)} ${start.format(TIME_FORMAT)} - ${end.format(TIME_FORMAT)}`;
    }

    // Different days
    return `${start.format(DATETIME_FORMAT)} - ${end.format(DATETIME_FORMAT)}`;
  } catch (error) {
    console.error("Error formatting date range:", error);
    return "Invalid date range";
  }
};

/**
 * Parses a date string with a specific format
 * @param dateString - Date string to parse
 * @param format - Format to use for parsing
 * @returns dayjs object or null if invalid
 */
export const parseDate = (dateString: string, format?: string) => {
  try {
    const parsed = format ? dayjs(dateString, format) : dayjs(dateString);
    return parsed.isValid() ? parsed : null;
  } catch (error) {
    console.error("Error parsing date:", error);
    return null;
  }
};

/**
 * Gets current timestamp in the standard format
 * @returns Current timestamp as formatted string
 */
export const getCurrentTimestamp = (): string => {
  return formatDateTime(new Date());
};

/**
 * Gets current date in the standard format
 * @returns Current date as formatted string
 */
export const getCurrentDate = (): string => {
  return formatDate(new Date());
};

// Export dayjs instance for advanced usage
export { dayjs };

// Export all functions as default object for convenient importing
export default {
  formatDateTime,
  formatDate,
  formatTime,
  formatRelativeTime,
  isToday,
  isYesterday,
  isOverdue,
  isFuture,
  formatDuration,
  startOfDay,
  endOfDay,
  formatDateRange,
  parseDate,
  getCurrentTimestamp,
  getCurrentDate,
  dayjs,
};
